import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { faCalendar as faCalendarDuotone, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { faCalendar as faCalendarRegular } from '@fortawesome/pro-regular-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import moment from 'moment';
import { combineLatest, Observable, Subject, timer } from 'rxjs';
import { map, share, startWith, takeUntil, tap } from 'rxjs/operators';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { AppointmentsState } from 'src/app/store/dashboard/appointments/appointments.state';
import { AppointmentTypeEnum } from 'src/app/store/dashboard/appointments/AppointmentTypeEnum';
import { OpenAppointmentDialog } from 'src/app/store/samwin/appointment-dialog/appointment-dialog.actions';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { AppointmentEntryRemoteDto } from 'src/generated/signalR/appointment-entry-remote-dto';

@Component({
   selector: 'samweb-appointments',
   templateUrl: './appointments.component.html',
   styleUrls: ['./appointments.component.scss'],
})
export class AppointmentsComponent implements OnInit, OnChanges, OnDestroy {
   // used to ensure appointment time display is updated regularly
   private static updateTimer$ = timer(0, 5000).pipe(startWith(0), share());

   @Input()
   public id: number;

   @Input()
   public brickInstanceId: Readonly<string>;

   public appointment$: Observable<AppointmentEntryRemoteDto>;
   public appointmentHasStarted = false;
   public appointmentType = AppointmentTypeEnum.FROM_TO;

   public readonly noAppointmentIcon: IconDefinition = faCalendarRegular;
   public readonly futureAppointmentIcon: IconDefinition = faCalendarDuotone;
   public readonly currentAppointmentIcon: IconDefinition = faCalendarDuotone;
   public readonly typeFromTo = AppointmentTypeEnum.FROM_TO;
   public readonly typeWholeDay = AppointmentTypeEnum.WHOLE_DAY;
   public readonly typeOpenEnded = AppointmentTypeEnum.OPEN_ENDED;

   private destroy$ = new Subject();

   public doShowExchangeAppointmentSubjects$: Observable<boolean>;

   constructor(private store: Store) {}

   // Is needed for virtual scrolling, the data would not update correctly otherwise.
   ngOnChanges(changes: SimpleChanges): void {
      if (changes?.id?.previousValue !== changes?.id?.currentValue) {
         this.ngOnInit();
      }
   }

   ngOnInit(): void {
      const currentAppointments$ = this.store.select(AppointmentsState.getAppointmentsForDirectoryEntry).pipe(
         map((getAppointmentsForDirectoryEntry) => getAppointmentsForDirectoryEntry(this.id)),
         takeUntil(this.destroy$)
      );

      // Appointments should only be visible starting from Multimedia Agent Package onwards.
      // Feature SAMWIN_7_EMAIL is used to detect Multimedia Agent or higher.
      const canSeeCurrentAppointment$ = this.store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_EMAIL)).pipe(takeUntil(this.destroy$));
      this.doShowExchangeAppointmentSubjects$ = this.store
         .select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.DO_SHOW_EXCHANGE_APPOINTMENT_SUBJECTS))
         .pipe(takeUntil(this.destroy$));

      this.appointment$ = combineLatest([currentAppointments$, canSeeCurrentAppointment$]).pipe(
         map(([appointments, canSeeCurrentAppointment]) => {
            if (canSeeCurrentAppointment) {
               const now = moment(moment.now()).toDate();
               return _(appointments).find((app) => app.end >= now);
            }
            return null;
         }),
         tap((appointment) => {
            if (_.isNil(appointment)) {
               return;
            }
            const now = moment(moment.now()).toDate();
            this.appointmentHasStarted = appointment.start <= now;
            this.appointmentType = AppointmentTypeEnum.fromAppointment(appointment);
         }),
         takeUntil(this.destroy$)
      );

      // Timer is only needed to check if appointment has already started or not.
      combineLatest([this.appointment$, AppointmentsComponent.updateTimer$])
         .pipe(takeUntil(this.destroy$))
         .subscribe(([appointment, _unused]) => {
            if (_.isNil(appointment)) {
               return;
            }
            const now = moment(moment.now()).toDate();
            this.appointmentHasStarted = appointment.start <= now;
            this.appointmentType = AppointmentTypeEnum.fromAppointment(appointment);
         });
   }

   openDetails() {
      this.store.dispatch(new OpenAppointmentDialog(this.id, this.brickInstanceId));
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
